import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Mail, Send, Users, AlertTriangle, Clock, CheckCircle2, XCircle, History } from 'lucide-react';

const API_URL = "";

export const EmailParticipantsModal = ({ open, onClose, sourceType, sourceId, sourceName, instructor, schedule, modalTitle }) => {
  const { token } = useAuth();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (open && sourceId) {
      fetchRecipients();
      fetchHistory();
      setSubject('');
      setMessage('');
      setConfirmStep(false);
      setResult(null);
      setShowHistory(false);
    }
  }, [open, sourceId]);

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/registrations/participants/${sourceType}/${sourceId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setRecipients(data.recipients || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/registrations/email-history/${sourceType}/${sourceId}`, { headers });
      if (res.ok) setHistory(await res.json());
    } catch (e) { /* ignore */ }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/registrations/bulk-email`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          source_type: sourceType,
          source_id: String(sourceId),
          subject,
          message,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult(data);
        toast.success(`Email sent to ${data.sent} participants`);
        fetchHistory();
      } else {
        toast.error(data.detail || 'Failed to send');
        setConfirmStep(false);
      }
    } catch (e) {
      toast.error('Send failed');
      setConfirmStep(false);
    }
    setSending(false);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

  // Result screen
  if (result) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Email Sent</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle2 className="text-green-600" size={24} />
              <div>
                <p className="font-medium text-green-800">Sent: {result.sent}</p>
                {result.failed > 0 && <p className="text-red-600 text-sm">Failed: {result.failed}</p>}
              </div>
            </div>
            {result.failures?.length > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                <p className="font-medium text-red-700 mb-1">Failed addresses:</p>
                {result.failures.map((f, i) => (
                  <p key={i} className="text-red-600">{f.email}: {f.error}</p>
                ))}
              </div>
            )}
            <Button onClick={onClose} className="w-full" data-testid="bulk-email-done-btn">Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Confirm screen
  if (confirmStep) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Confirm Send</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="text-amber-600 flex-shrink-0" size={24} />
              <p className="text-amber-800 font-medium">
                Send email to {recipients.length} participant{recipients.length !== 1 ? 's' : ''}?
              </p>
            </div>
            <div className="text-sm space-y-1 text-stone-600">
              <p><strong>Subject:</strong> {subject}</p>
              <p><strong>To:</strong> {sourceName}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setConfirmStep(false)} className="flex-1" disabled={sending}>
                Back
              </Button>
              <Button onClick={handleSend} disabled={sending} className="flex-1 bg-cafa-red hover:bg-cafa-red/90" data-testid="bulk-email-confirm-send">
                {sending ? 'Sending...' : `Send to ${recipients.length}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Compose screen
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail size={20} /> {modalTitle || 'Email Participants'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Context bar */}
          <div className="p-3 bg-stone-50 border border-stone-200 rounded-lg text-sm space-y-1" data-testid="bulk-email-context">
            <p className="font-medium text-stone-900">{sourceName}</p>
            {instructor && <p className="text-stone-500">Instructor: {instructor}</p>}
            {schedule && <p className="text-stone-500">Schedule: {schedule}</p>}
            <div className="flex items-center gap-1.5 mt-1">
              <Users size={14} className="text-stone-400" />
              {loading ? (
                <span className="text-stone-400">Loading recipients...</span>
              ) : (
                <span className="text-stone-600 font-medium">{recipients.length} eligible recipient{recipients.length !== 1 ? 's' : ''}</span>
              )}
            </div>
          </div>

          {recipients.length === 0 && !loading ? (
            <div className="text-center py-6 text-stone-500">
              <Users size={32} className="mx-auto mb-2 text-stone-300" />
              <p>No eligible participants found.</p>
              <p className="text-xs mt-1">Only paid/enrolled participants receive emails.</p>
            </div>
          ) : (
            <>
              {/* Subject */}
              <div>
                <Label>Subject *</Label>
                <Input
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder={`Update regarding ${sourceName}`}
                  className="mt-1"
                  data-testid="bulk-email-subject"
                />
              </div>

              {/* Message */}
              <div>
                <Label>Message *</Label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={8}
                  className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-cafa-gold/40 focus:outline-none"
                  placeholder="Write your message here. HTML formatting is supported."
                  data-testid="bulk-email-message"
                />
                <p className="text-xs text-stone-400 mt-1">
                  Each email is personalized with the participant's name and class/workshop context.
                </p>
              </div>

              {/* Recipient preview */}
              {recipients.length > 0 && (
                <details className="border border-stone-200 rounded-lg">
                  <summary className="px-3 py-2 text-sm text-stone-600 cursor-pointer hover:bg-stone-50">
                    Preview recipients ({recipients.length})
                  </summary>
                  <div className="px-3 pb-3 max-h-32 overflow-y-auto text-xs text-stone-500 space-y-0.5">
                    {recipients.map((r, i) => (
                      <div key={i}>{r.student_name || r.parent_name} — {r.email}</div>
                    ))}
                  </div>
                </details>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700"
                  data-testid="bulk-email-history-toggle"
                >
                  <History size={14} /> Email History
                </button>
                <Button
                  onClick={() => setConfirmStep(true)}
                  disabled={!subject.trim() || !message.trim() || recipients.length === 0}
                  className="bg-cafa-red hover:bg-cafa-red/90"
                  data-testid="bulk-email-send-btn"
                >
                  <Send size={16} className="mr-1.5" /> Send Email
                </Button>
              </div>
            </>
          )}

          {/* History */}
          {showHistory && history.length > 0 && (
            <div className="border-t border-stone-200 pt-3 mt-2">
              <p className="text-sm font-medium text-stone-700 mb-2">Previous Emails</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {history.map((h, i) => (
                  <div key={i} className="p-2.5 bg-stone-50 rounded-lg text-sm border border-stone-100">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-stone-800 truncate flex-1">{h.subject}</p>
                      <span className="text-xs text-stone-400 ml-2 flex-shrink-0">{formatDate(h.sent_at)}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                      <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-green-500" /> {h.sent_count} sent</span>
                      {h.failed_count > 0 && <span className="flex items-center gap-1"><XCircle size={12} className="text-red-500" /> {h.failed_count} failed</span>}
                      <span>by {h.sent_by}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {showHistory && history.length === 0 && (
            <p className="text-sm text-stone-400 text-center py-2 border-t border-stone-200 mt-2">No emails sent yet.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
